import { sha256 } from '@noble/hashes/sha2.js';

/**
 * The proof of work the server may ask for before a new session (SPEC.md §7.2): a nonce such that
 * SHA-256(SHA-256(challenge) ":" nonce) starts with `bits` zero bits. A reader under the soft limits
 * is never asked; at the base difficulty (11 bits) it is ~2 000 one-block hashes, a few ms. Works in slices so the page
 * stays responsive.
 */
export async function solvePow(challenge: string, bits: number, cancelled: () => boolean = () => false): Promise<string | null> {
  // One SHA-256 block per try: SHA-256(SHA-256(challenge) | ":" | nonce).
  const enc = new TextEncoder();
  const buf = new Uint8Array(32 + 1 + 12);
  buf.set(sha256(enc.encode(challenge)));
  buf[32] = 58;
  for (let n = 0; ; ) {
    const until = n + 8192;
    for (; n < until; n++) {
      const nonce = n.toString(36);
      const len = 33 + enc.encodeInto(nonce, buf.subarray(33)).written;
      if (zeroBits(sha256(buf.subarray(0, len))) >= bits) return nonce;
    }
    await new Promise((r) => setTimeout(r, 0));
    if (cancelled()) return null;
  }
}

function zeroBits(h: Uint8Array): number {
  let n = 0;
  for (const b of h) {
    if (b === 0) {
      n += 8;
      continue;
    }
    return n + Math.clz32(b) - 24;
  }
  return n;
}

